import React, { type ReactNode, useContext } from 'react';

import { ReactRelayContext, type Environment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

interface RelayProviderProps {
	children: ReactNode;
}

/**
 * Standalone Relay Environment Provider for conversation-assistant-viz.
 *
 * Gated behind the `ai_mate_viz_relay_provider` feature flag. When disabled,
 * this component simply renders its children without any relay provider.
 *
 * When enabled and embedded in products (Jira, Confluence, etc.) that already
 * provide their own RelayEnvironmentProvider, this component will skip creating
 * a new provider and let the children use the existing context.
 *
 * When enabled and no existing Relay environment is found in the context, this
 * component wraps children with the AtlassianRelayEnvironmentProvider.
 *
 * Usage:
 * ```tsx
 * import { RelayProvider } from '@atlassian/conversation-assistant-viz/relay';
 *
 * <RelayProvider>
 *   <App />
 * </RelayProvider>
 * ```
 */
export const RelayProvider = ({ children }: RelayProviderProps): JSX.Element => {
	const existingEnvironment = useContext(ReactRelayContext)?.environment;

	if (fg('ai_mate_viz_relay_provider')) {
		if (existingEnvironment) {
			return <>{children}</>;
		}

		return <AtlassianRelayEnvironmentProvider>{children}</AtlassianRelayEnvironmentProvider>;
	}

	return <>{children}</>;
};

export default RelayProvider;

/**
 * Safely gets the Relay environment from the context without throwing.
 *
 * Returns the environment only when the `ai_mate_viz_relay_provider` feature flag is enabled.
 * This ensures relay queries are not used until the flag is turned on, even if a host product
 * (Jira, Confluence, etc.) already provides a relay environment higher up the tree.
 *
 * Unlike `useRelayEnvironment()` from react-relay which throws if not inside a RelayEnvironmentProvider,
 * this hook returns `null` when no provider is available or when the flag is off.
 */
export function useRelayEnvironment(): Environment | null {
	const context = useContext(ReactRelayContext);

	if (!fg('ai_mate_viz_relay_provider')) {
		return null;
	}

	return context?.environment ?? null;
}
